/**
 * Unified tool type definitions shared across all agents.
 */

import {CitationGenerationMode} from 'graphql-client/__generated/graphqlGlobalTypes';
import type {DisplayMessageChatEventNode, LLMToolCallBlock} from 'llm/chat-library/messages/MessageTypes';
import {SKIPPED_TOOL_DISPLAY_LABEL, ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {isNativeLoadSkill} from 'llm/chat-library/tool-details/LoadSkillToolCallParser';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/** A tool icon source is either a named glyph or an image URL with server name for alt text. */
export type ToolIconSource = {iconName: BenchlingIconName} | {iconUrl: string; serverName: string};

export function isGlyphIcon(icon: ToolIconSource): icon is {iconName: BenchlingIconName} {
  return (icon as {iconName?: unknown}).iconName != null;
}

// ============================================================================
// Tool Type Enum
// ============================================================================

/**
 * All tool types used across agents (Deep Research, Data Capture, etc.).
 *
 * When adding tools, also update:
 * - TOOL_DISPLAY_NAMES_IN_PROGRESS / TOOL_DISPLAY_NAMES_IMPERATIVE / TOOL_DISPLAY_NAMES_COMPLETED
 * - TOOL_ICONS
 * - TOOLS_TO_DISPLAY (if the tool should appear in the progress UI)
 *
 * TOOL_DISPLAY_NAMES_* and TOOL_ICONS are typed Record<ToolType, ...>, so the compiler forces you
 * to fill them in for every member added here -- but adding the member itself is not enforced by
 * the compiler. A tool whose class name (its wire name; see Tool.tool_name in benchling/llm) is
 * missing from this enum silently falls back to a generic "Calling/Called <name>" step and does
 * NOT render in the progress UI at all (see shouldDisplayTool), even though its maps entries (if
 * any) are never used. See tests/unit/llm/tool_details_frontend_wiring_test.py for the guard that
 * catches an approval-gated backend write tool missing from this enum.
 *
 * Keep in sync with backend toolkit definitions in benchling/llm.
 */
export enum ToolType {
  // --- Shared tools (used by multiple agents) ---
  ToolSearch = 'ToolSearch',
  Think = 'Think',
  IdsToModelsTool = 'IdsToModelsTool',
  SummarizeDocument = 'SummarizeDocument',
  // Legacy wire name of SummarizeDocument. Stored chat events recorded before the rename still carry
  // it, so keep this member to render them. Do not remove it or repurpose the wire name.
  ReadBenchlingDocumentLegacy = 'ReadBenchlingDocument',
  SaveArtifact = 'SaveArtifact',
  ListArtifacts = 'ListArtifacts',
  ViewArtifact = 'ViewArtifact',
  LoadFileAsArtifact = 'LoadFileAsArtifact',
  LoadDocumentAttachmentAsArtifact = 'LoadDocumentAttachmentAsArtifact',
  SubstituteInArtifact = 'SubstituteInArtifact',
  UploadFileToSandbox = 'UploadFileToSandbox',
  LoadDropdownOptions = 'LoadDropdownOptions',
  HelpDocs = 'HelpDocs',
  LoadPackageDocs = 'LoadPackageDocs',
  AskWarehouse = 'AskWarehouse',
  WebSearch = 'WebSearch',
  SleepTool = 'SleepTool',

  // --- Deep Research: supervisor-level tools ---
  ResearchSubAgent = 'ResearchSubAgent',
  CompleteAnswer = 'CompleteAnswer',
  PublicDataSubAgent = 'PublicDataSubAgent',

  // --- Deep Research: sub-agent tools ---
  CompleteAnswerTool = 'CompleteAnswerTool',
  DeepResearchTool = 'DeepResearchTool',
  SearchTool = 'SearchTool',
  GetWarehouseOverviewTool = 'GetWarehouseOverviewTool',
  SearchNotebookEntries = 'SearchNotebookEntries',
  SearchSequencesTool = 'SearchSequencesTool',
  SearchChemicalsTool = 'SearchChemicalsTool',
  QueryWarehouseTool = 'QueryWarehouseTool',
  AttachmentTool = 'AttachmentTool',
  ReadBlob = 'ReadBlob',
  PlottingTool = 'PlottingTool',
  GenerateImageTool = 'GenerateImageTool',
  CustomCodeTool = 'CustomCodeTool',
  ExecuteCustomCodePipelineStep = 'ExecuteCustomCodePipelineStep',
  RunCodeTool = 'RunCodeTool',

  // --- Skills tools ---
  LoadSkill = 'LoadSkill',
  ManageSkill = 'ManageSkill',

  // --- Analysis & automation template tools ---
  CreateAnalysisPlan = 'CreateAnalysisPlan',
  CreateAnalysis = 'CreateAnalysis',
  ApplyTemplateToAnalysis = 'ApplyTemplateToAnalysis',
  CreateAutomationTemplate = 'CreateAutomationTemplate',
  EditAutomationTemplate = 'EditAutomationTemplate',
  EditAnalysis = 'EditAnalysis',

  // --- Data Capture specific tools ---
  Respond = 'Respond',
  // ReadTargetDocument is the legacy wire name for the tool now called ReadDocument. Stored chat
  // events retain whatever tool name was current when they were recorded, so this member (and its
  // display/icon entries below) must stay to keep rendering historical tool calls. Do not remove
  // it or repurpose the 'ReadTargetDocument' wire name for a different tool.
  ReadTargetDocument = 'ReadTargetDocument',
  ReadDocument = 'ReadDocument',
  ReadDraftDocument = 'ReadDraftDocument',
  AskQuestion = 'AskQuestion',
  InstantiateDocument = 'InstantiateDocument',
  SearchEntriesAndTemplates = 'SearchEntriesAndTemplates',
  SearchBenchlingObjects = 'SearchBenchlingObjects',
  TranscribeFile = 'TranscribeFile',
  ReviewTool = 'ReviewTool',
  EditTableCells = 'EditTableCells',
  // Value is the dynamically generated wire name from create_update_document_tool's
  // create_model("UpdateDocumentContent", ...) call, not a TS-side choice.
  UpdateDocumentContent = 'UpdateDocumentContent',
  // Legacy alias: create_update_document_tool's create_model(...) call used to be named
  // "UpdateDocument" (see benchling/llm/data_capture_assistant/agent/data_capture_agent.py before
  // commit 363fb630a94f renamed it to "UpdateDocumentContent" on 2026-04-06). Chat events recorded
  // before that rename still carry the old tool_name, so keep this member (same precedent as
  // ReadBenchlingDocumentLegacy's enum value differing from its identifier) to render them correctly
  // instead of falling back to a generic step.
  UpdateDocumentLegacy = 'UpdateDocument',
  InsertDocumentContent = 'InsertDocumentContent',
  SearchFiles = 'SearchFiles',
  SearchSchemas = 'SearchSchemas',
  FillTables = 'FillTables',
  ReadFiles = 'ReadFiles',
  CreateLookupConfig = 'CreateLookupConfig',

  // --- Schema administration tools ---
  CreateDropdownConfig = 'CreateDropdownConfig',
  CreateCustomEntitySchema = 'CreateCustomEntitySchema',
  CreateRunSchema = 'CreateRunSchema',

  // --- Agent tools ---
  // ManageRoutine is the legacy wire name for the tool now called ManageAgent. Stored chat events
  // retain whatever tool name was current when they were recorded, so this member (and its
  // display/icon entries below) must stay to keep rendering historical tool calls. Do not remove
  // it or repurpose the 'ManageRoutine' wire name for a different tool.
  ManageRoutine = 'ManageRoutine',
  ManageAgent = 'ManageAgent',
  RunAgentDirectly = 'RunAgentDirectly',

  // --- Co-scientist tools ---
  ListFolderContents = 'ListFolderContents',
  CreateFolder = 'CreateFolder',
  CreateEntry = 'CreateEntry',
  CreateFile = 'CreateFile',
  UpdateFile = 'UpdateFile',
  ReadFile = 'ReadFile',
  CreateAASequences = 'CreateAASequences',
  CreateDNASequences = 'CreateDNASequences',
  CreateMolecule = 'CreateMolecule',
  CreateCustomEntities = 'CreateCustomEntities',
  // Bulk update tool for custom entities (paired with CreateCustomEntities).
  UpdateCustomEntities = 'UpdateCustomEntities',
  CreateWorkflowTaskGroups = 'CreateWorkflowTaskGroups',
  UpdateWorkflowTaskGroups = 'UpdateWorkflowTaskGroups',
  CreateWorkflowTasks = 'CreateWorkflowTasks',
  UpdateWorkflowTasks = 'UpdateWorkflowTasks',
  ExecuteWorkflowTasksIntoEntry = 'ExecuteWorkflowTasksIntoEntry',
  ArchiveItem = 'ArchiveItem',
  RenameItem = 'RenameItem',
  MoveItemToFolder = 'MoveItemToFolder',
  SetTargetEntry = 'SetTargetEntry',
  AddExistingEntitiesToTable = 'AddExistingEntitiesToTable',
  AddExistingResultsToTable = 'AddExistingResultsToTable',
  // CreateDocumentTool is the legacy wire name for the tool now called CreateDraftDocument.
  // Stored chat events retain whatever tool name was current when they were recorded, so this
  // member (and its display/icon entries below) must stay to keep rendering historical tool
  // calls. Do not remove it or repurpose the 'CreateDocumentTool' wire name for a different tool.
  CreateDocumentTool = 'CreateDocumentTool',
  CreateDraftDocument = 'CreateDraftDocument',
  InvokeToolsFromFile = 'InvokeToolsFromFile',
  RegisterEntities = 'RegisterEntities',
  TransferIntoContainers = 'TransferIntoContainers',
  CreateContainers = 'CreateContainers',
  CreateBoxes = 'CreateBoxes',
  UpdateContainerFields = 'UpdateContainerFields',
  UpdateContainerLocations = 'UpdateContainerLocations',
  CreatePlates = 'CreatePlates',
  CreateResults = 'CreateResults',
  StartChatSession = 'StartChatSession',
  GetChatSession = 'GetChatSession',
  ListChatSessions = 'ListChatSessions',
  ScheduleForFuture = 'ScheduleForFuture',
  AskUser = 'AskUser',
  CouncilTool = 'CouncilTool',
  CreateProcedureRunPlan = 'CreateProcedureRunPlan',
  ValidateProcedureRunPlan = 'ValidateProcedureRunPlan',
  PromoteProcedureRunPlan = 'PromoteProcedureRunPlan',
  UpdateProcedureRunPlanConditions = 'UpdateProcedureRunPlanConditions',
  AddProcedureRunPlanConditions = 'AddProcedureRunPlanConditions',
  DeleteProcedureRunPlanConditions = 'DeleteProcedureRunPlanConditions',
  UpdateProcedureRunPlanConditionReplicates = 'UpdateProcedureRunPlanConditionReplicates',
  CreateProcedureRunParameterValue = 'CreateProcedureRunParameterValue',
  CreateLabAutomationRun = 'CreateLabAutomationRun',
  ExecuteLabAutomationRun = 'ExecuteLabAutomationRun',
  GenerateLegacyLabAutomationInput = 'GenerateLegacyLabAutomationInput',
  ProcessLegacyLabAutomationOutput = 'ProcessLegacyLabAutomationOutput',
  GetLegacyLabAutomationOutputProcessingStatus = 'GetLegacyLabAutomationOutputProcessingStatus',

  // --- Structure prediction tools ---
  ListStructurePredictionModels = 'ListStructurePredictionModels',
  StartStructurePredictionBatch = 'StartStructurePredictionBatch',
  StartProteinRegistration = 'StartProteinRegistration',
  GetStructurePredictionBatchResults = 'GetStructurePredictionBatchResults',

  // --- Snowflake gateway tools ---
  ListSnowflakeTables = 'ListSnowflakeTables',
  ExportSnowflakeTableToDataset = 'ExportSnowflakeTableToDataset',
  GetSnowflakeExportStatus = 'GetSnowflakeExportStatus',

  // --- Canvas tools ---
  CreateCanvasFromHtml = 'CreateCanvasFromHtml',
  UpdateCanvasHtml = 'UpdateCanvasHtml',

  // --- Opentrons lab automation tools ---
  UploadOpentronsProtocol = 'UploadOpentronsProtocol',
  CreateOpentronsRun = 'CreateOpentronsRun',
  StartOpentronsRun = 'StartOpentronsRun',
  // --- Tecan lab automation tools ---
  RunTecanMethod = 'RunTecanMethod',
  GetTecanOutputFiles = 'GetTecanOutputFiles',
}

export const RESPONSE_TOOLS: Array<ToolType> = [ToolType.CompleteAnswer, ToolType.Respond];

// ============================================================================
// Display Names
// ============================================================================

/**
 * Human-readable display names shown while a tool is running (present tense).
 * Tools that are not displayed in the progress UI have null values.
 */
export const TOOL_DISPLAY_NAMES_IN_PROGRESS: Record<ToolType, string | null> = {
  // Shared
  [ToolType.ToolSearch]: 'Loading tools',
  [ToolType.Think]: 'Thinking',
  [ToolType.IdsToModelsTool]: 'Looking things up',
  [ToolType.SummarizeDocument]: 'Reading notebook',
  [ToolType.ReadBenchlingDocumentLegacy]: 'Reading notebook',
  [ToolType.SaveArtifact]: 'Saving artifact',
  [ToolType.ListArtifacts]: 'Listing artifacts',
  [ToolType.ViewArtifact]: 'Viewing artifact',
  [ToolType.LoadFileAsArtifact]: 'Loading file as artifact',
  [ToolType.LoadDocumentAttachmentAsArtifact]: 'Loading attachment as artifact',
  [ToolType.SubstituteInArtifact]: 'Editing artifact',
  [ToolType.UploadFileToSandbox]: 'Uploading file to sandbox',
  [ToolType.LoadDropdownOptions]: 'Loading dropdown options',
  [ToolType.HelpDocs]: 'Searching help docs',
  [ToolType.LoadPackageDocs]: 'Loading package docs',
  [ToolType.AskWarehouse]: 'Asking warehouse',
  [ToolType.WebSearch]: 'Searching the web',
  [ToolType.SleepTool]: 'Waiting',

  // Deep Research
  [ToolType.ResearchSubAgent]: 'Starting research subagent',
  [ToolType.CompleteAnswer]: 'Generating citations',
  [ToolType.CompleteAnswerTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.DeepResearchTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.PublicDataSubAgent]: 'Searching public data',
  [ToolType.SearchTool]: 'Searching Benchling',
  [ToolType.GetWarehouseOverviewTool]: 'Getting warehouse overview',
  [ToolType.SearchNotebookEntries]: 'Searching notebook entries',
  [ToolType.SearchSequencesTool]: 'Searching sequences',
  [ToolType.SearchChemicalsTool]: 'Searching chemicals',
  [ToolType.QueryWarehouseTool]: 'Querying warehouse',
  [ToolType.AttachmentTool]: 'Reading attachment',
  [ToolType.ReadBlob]: 'Reading blob',
  [ToolType.PlottingTool]: 'Creating chart',
  [ToolType.GenerateImageTool]: 'Generating image',
  [ToolType.CustomCodeTool]: 'Running code',
  [ToolType.ExecuteCustomCodePipelineStep]: 'Executing Benchling Custom Code',
  [ToolType.RunCodeTool]: 'Running code',

  // Skills
  [ToolType.LoadSkill]: 'Reading skill',
  [ToolType.ManageSkill]: 'Managing skill',

  // Analysis & automation template tools
  [ToolType.CreateAnalysisPlan]: 'Creating analysis plan',
  [ToolType.CreateAnalysis]: 'Creating analysis',
  [ToolType.ApplyTemplateToAnalysis]: 'Applying template to analysis',
  [ToolType.CreateAutomationTemplate]: 'Creating automation template',
  [ToolType.EditAutomationTemplate]: 'Editing automation template',
  [ToolType.EditAnalysis]: 'Editing analysis',

  // Data Capture
  [ToolType.Respond]: 'Finalizing response',
  // Legacy wire name; see the ToolType enum comment.
  [ToolType.ReadTargetDocument]: 'Reading document',
  [ToolType.ReadDocument]: 'Reading document',
  [ToolType.ReadDraftDocument]: 'Reading document',
  [ToolType.AskQuestion]: 'Asking question',
  [ToolType.InstantiateDocument]: 'Instantiating document',
  [ToolType.SearchEntriesAndTemplates]: 'Searching entries and templates',
  [ToolType.SearchBenchlingObjects]: 'Searching Benchling objects',
  [ToolType.TranscribeFile]: 'Transcribing file',
  [ToolType.ReviewTool]: 'Reviewing',
  [ToolType.EditTableCells]: 'Editing table cells',
  [ToolType.UpdateDocumentContent]: 'Updating document',
  [ToolType.UpdateDocumentLegacy]: 'Updating document',
  [ToolType.InsertDocumentContent]: 'Inserting document content',
  [ToolType.SearchFiles]: 'Searching file content',
  [ToolType.SearchSchemas]: 'Searching schemas',
  [ToolType.FillTables]: 'Filling tables',
  [ToolType.ReadFiles]: 'Reading files',
  [ToolType.CreateLookupConfig]: 'Creating lookup configuration',

  // Schema administration tools
  [ToolType.CreateDropdownConfig]: 'Creating dropdown configuration',
  [ToolType.CreateCustomEntitySchema]: 'Creating custom entity schema',
  [ToolType.CreateRunSchema]: 'Creating run schema',

  // Agent tools
  [ToolType.ManageRoutine]: 'Managing agent',
  [ToolType.ManageAgent]: 'Managing agent',
  [ToolType.RunAgentDirectly]: 'Starting agent run',

  // Co-scientist tools
  [ToolType.ListFolderContents]: 'Listing folder contents',
  [ToolType.CreateFolder]: 'Creating folder',
  [ToolType.CreateEntry]: 'Creating notebook entry',
  [ToolType.CreateFile]: 'Creating file',
  [ToolType.UpdateFile]: 'Updating file',
  [ToolType.ReadFile]: 'Reading file',
  [ToolType.CreateAASequences]: 'Creating AA sequences',
  [ToolType.CreateDNASequences]: 'Creating DNA sequences',
  [ToolType.CreateMolecule]: 'Creating molecule',
  [ToolType.CreateCustomEntities]: 'Creating custom entities',
  [ToolType.UpdateCustomEntities]: 'Updating custom entities',
  [ToolType.CreateWorkflowTaskGroups]: 'Creating workflow task groups',
  [ToolType.UpdateWorkflowTaskGroups]: 'Updating workflow task groups',
  [ToolType.CreateWorkflowTasks]: 'Creating workflow tasks',
  [ToolType.UpdateWorkflowTasks]: 'Updating workflow tasks',
  [ToolType.ExecuteWorkflowTasksIntoEntry]: 'Executing workflow tasks',
  [ToolType.ArchiveItem]: 'Archiving item',
  [ToolType.RenameItem]: 'Renaming item',
  [ToolType.MoveItemToFolder]: 'Moving item',
  [ToolType.SetTargetEntry]: 'Setting target entry',
  [ToolType.AddExistingEntitiesToTable]: 'Adding entities to table',
  [ToolType.AddExistingResultsToTable]: 'Adding results to table',
  // Legacy wire name; see the ToolType enum comment.
  [ToolType.CreateDocumentTool]: 'Creating document',
  [ToolType.CreateDraftDocument]: 'Creating document',
  [ToolType.InvokeToolsFromFile]: 'Invoking tools from file',
  [ToolType.RegisterEntities]: 'Registering entities',
  [ToolType.TransferIntoContainers]: 'Transferring into containers',
  [ToolType.CreateContainers]: 'Creating containers',
  [ToolType.CreateBoxes]: 'Creating boxes',
  [ToolType.UpdateContainerFields]: 'Updating container fields',
  [ToolType.UpdateContainerLocations]: 'Moving containers',
  [ToolType.CreatePlates]: 'Creating plates',
  [ToolType.CreateResults]: 'Creating results',
  [ToolType.StartChatSession]: 'Starting sub-agent session',
  [ToolType.GetChatSession]: 'Checking sub-agent session',
  [ToolType.ListChatSessions]: 'Listing chat sessions',
  [ToolType.ScheduleForFuture]: 'Scheduling for later',
  [ToolType.AskUser]: 'Asking you questions',
  [ToolType.CouncilTool]: 'Thinking deeply',
  [ToolType.CreateProcedureRunPlan]: 'Creating run plan',
  [ToolType.ValidateProcedureRunPlan]: 'Checking run plan for errors',
  [ToolType.PromoteProcedureRunPlan]: 'Creating run',
  [ToolType.UpdateProcedureRunPlanConditions]: 'Updating run plan conditions',
  [ToolType.AddProcedureRunPlanConditions]: 'Adding run plan conditions',
  [ToolType.DeleteProcedureRunPlanConditions]: 'Deleting run plan conditions',
  [ToolType.UpdateProcedureRunPlanConditionReplicates]: 'Updating run plan condition replicates',
  [ToolType.CreateProcedureRunParameterValue]: 'Recording parameter value',
  [ToolType.CreateLabAutomationRun]: 'Creating lab automation run',
  [ToolType.ExecuteLabAutomationRun]: 'Executing lab automation run',
  [ToolType.GenerateLegacyLabAutomationInput]: 'Generating automation input',
  [ToolType.ProcessLegacyLabAutomationOutput]: 'Processing automation output',
  [ToolType.GetLegacyLabAutomationOutputProcessingStatus]: 'Checking automation output status',

  // Structure prediction tools
  [ToolType.ListStructurePredictionModels]: 'Listing structure prediction models',
  [ToolType.StartStructurePredictionBatch]: 'Starting structure prediction',
  [ToolType.StartProteinRegistration]: 'Starting protein registration',
  [ToolType.GetStructurePredictionBatchResults]: 'Checking structure predictions',
  [ToolType.ListSnowflakeTables]: 'Listing Snowflake tables',
  [ToolType.ExportSnowflakeTableToDataset]: 'Starting Snowflake export',
  [ToolType.GetSnowflakeExportStatus]: 'Checking Snowflake export',

  // Canvas tools
  [ToolType.CreateCanvasFromHtml]: 'Creating canvas',
  [ToolType.UpdateCanvasHtml]: 'Updating canvas',

  // Opentrons lab automation tools
  [ToolType.UploadOpentronsProtocol]: 'Uploading Opentrons protocol',
  [ToolType.CreateOpentronsRun]: 'Creating Opentrons run',
  [ToolType.StartOpentronsRun]: 'Starting Opentrons run',
  // Tecan lab automation tools
  [ToolType.RunTecanMethod]: 'Starting Tecan method',
  [ToolType.GetTecanOutputFiles]: 'Retrieving Tecan output files',
};

/**
 * Status-independent task labels shown in imperative-mood progress timelines.
 * Tools that are not displayed in the progress UI have null values.
 */
export const TOOL_DISPLAY_NAMES_IMPERATIVE: Record<ToolType, string | null> = {
  // Shared
  [ToolType.ToolSearch]: 'Load tools',
  [ToolType.Think]: 'Think',
  [ToolType.IdsToModelsTool]: 'Look things up',
  [ToolType.SummarizeDocument]: 'Read notebook',
  [ToolType.ReadBenchlingDocumentLegacy]: 'Read notebook',
  [ToolType.SaveArtifact]: 'Save artifact',
  [ToolType.ListArtifacts]: 'List artifacts',
  [ToolType.ViewArtifact]: 'View artifact',
  [ToolType.LoadFileAsArtifact]: 'Load file as artifact',
  [ToolType.LoadDocumentAttachmentAsArtifact]: 'Load attachment as artifact',
  [ToolType.SubstituteInArtifact]: 'Edit artifact',
  [ToolType.UploadFileToSandbox]: 'Upload file to sandbox',
  [ToolType.LoadDropdownOptions]: 'Load dropdown options',
  [ToolType.HelpDocs]: 'Search help docs',
  [ToolType.LoadPackageDocs]: 'Load package docs',
  [ToolType.AskWarehouse]: 'Ask warehouse',
  [ToolType.WebSearch]: 'Search the web',
  [ToolType.SleepTool]: 'Wait',

  // Deep Research
  [ToolType.ResearchSubAgent]: 'Start research subagent',
  [ToolType.CompleteAnswer]: 'Generate citations',
  [ToolType.CompleteAnswerTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.DeepResearchTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.PublicDataSubAgent]: 'Search public data',
  [ToolType.SearchTool]: 'Search Benchling',
  [ToolType.GetWarehouseOverviewTool]: 'Get warehouse overview',
  [ToolType.SearchNotebookEntries]: 'Search notebook entries',
  [ToolType.SearchSequencesTool]: 'Search sequences',
  [ToolType.SearchChemicalsTool]: 'Search chemicals',
  [ToolType.QueryWarehouseTool]: 'Query warehouse',
  [ToolType.AttachmentTool]: 'Read attachment',
  [ToolType.ReadBlob]: 'Read blob',
  [ToolType.PlottingTool]: 'Create chart',
  [ToolType.GenerateImageTool]: 'Generate image',
  [ToolType.CustomCodeTool]: 'Run code',
  [ToolType.ExecuteCustomCodePipelineStep]: 'Execute Benchling Custom Code',
  [ToolType.RunCodeTool]: 'Run code',

  // Skills
  [ToolType.LoadSkill]: 'Read skill',
  [ToolType.ManageSkill]: 'Manage skill',

  // Analysis & automation template tools
  [ToolType.CreateAnalysisPlan]: 'Create analysis plan',
  [ToolType.CreateAnalysis]: 'Create analysis',
  [ToolType.ApplyTemplateToAnalysis]: 'Apply template to analysis',
  [ToolType.CreateAutomationTemplate]: 'Create automation template',
  [ToolType.EditAutomationTemplate]: 'Edit automation template',
  [ToolType.EditAnalysis]: 'Edit analysis',

  // Data Capture
  [ToolType.Respond]: 'Finalize response',
  // Legacy wire name; see the ToolType enum comment.
  [ToolType.ReadTargetDocument]: 'Read document',
  [ToolType.ReadDocument]: 'Read document',
  [ToolType.ReadDraftDocument]: 'Read document',
  [ToolType.AskQuestion]: 'Ask question',
  [ToolType.InstantiateDocument]: 'Instantiate document',
  [ToolType.SearchEntriesAndTemplates]: 'Search entries and templates',
  [ToolType.SearchBenchlingObjects]: 'Search Benchling objects',
  [ToolType.TranscribeFile]: 'Transcribe file',
  [ToolType.ReviewTool]: 'Review',
  [ToolType.EditTableCells]: 'Edit table cells',
  [ToolType.UpdateDocumentContent]: 'Update document',
  [ToolType.UpdateDocumentLegacy]: 'Update document',
  [ToolType.InsertDocumentContent]: 'Insert document content',
  [ToolType.SearchFiles]: 'Search file content',
  [ToolType.SearchSchemas]: 'Search schemas',
  [ToolType.FillTables]: 'Fill tables',
  [ToolType.ReadFiles]: 'Read files',
  [ToolType.CreateLookupConfig]: 'Create lookup configuration',

  // Schema administration tools
  [ToolType.CreateDropdownConfig]: 'Create dropdown configuration',
  [ToolType.CreateCustomEntitySchema]: 'Create custom entity schema',
  [ToolType.CreateRunSchema]: 'Create run schema',

  // Agent tools
  [ToolType.ManageRoutine]: 'Manage agent',
  [ToolType.ManageAgent]: 'Manage agent',
  [ToolType.RunAgentDirectly]: 'Start agent run',

  // Co-scientist tools
  [ToolType.ListFolderContents]: 'List folder contents',
  [ToolType.CreateFolder]: 'Create folder',
  [ToolType.CreateEntry]: 'Create notebook entry',
  [ToolType.CreateFile]: 'Create file',
  [ToolType.UpdateFile]: 'Update file',
  [ToolType.ReadFile]: 'Read file',
  [ToolType.CreateAASequences]: 'Create AA sequences',
  [ToolType.CreateDNASequences]: 'Create DNA sequences',
  [ToolType.CreateMolecule]: 'Create molecule',
  [ToolType.CreateCustomEntities]: 'Create custom entities',
  [ToolType.UpdateCustomEntities]: 'Update custom entities',
  [ToolType.CreateWorkflowTaskGroups]: 'Create workflow task groups',
  [ToolType.UpdateWorkflowTaskGroups]: 'Update workflow task groups',
  [ToolType.CreateWorkflowTasks]: 'Create workflow tasks',
  [ToolType.UpdateWorkflowTasks]: 'Update workflow tasks',
  [ToolType.ExecuteWorkflowTasksIntoEntry]: 'Execute workflow tasks',
  [ToolType.ArchiveItem]: 'Archive item',
  [ToolType.RenameItem]: 'Rename item',
  [ToolType.MoveItemToFolder]: 'Move item',
  [ToolType.SetTargetEntry]: 'Set target entry',
  [ToolType.AddExistingEntitiesToTable]: 'Add entities to table',
  [ToolType.AddExistingResultsToTable]: 'Add results to table',
  // Legacy wire name; see the ToolType enum comment.
  [ToolType.CreateDocumentTool]: 'Create document',
  [ToolType.CreateDraftDocument]: 'Create document',
  [ToolType.InvokeToolsFromFile]: 'Invoke tools from file',
  [ToolType.RegisterEntities]: 'Register entities',
  [ToolType.TransferIntoContainers]: 'Transfer into containers',
  [ToolType.CreateContainers]: 'Create containers',
  [ToolType.CreateBoxes]: 'Create boxes',
  [ToolType.UpdateContainerFields]: 'Update container fields',
  [ToolType.UpdateContainerLocations]: 'Move containers',
  [ToolType.CreatePlates]: 'Create plates',
  [ToolType.CreateResults]: 'Create results',
  [ToolType.StartChatSession]: 'Start sub-agent session',
  [ToolType.GetChatSession]: 'Check sub-agent session',
  [ToolType.ListChatSessions]: 'List chat sessions',
  [ToolType.ScheduleForFuture]: 'Schedule for later',
  [ToolType.AskUser]: 'Ask you questions',
  [ToolType.CouncilTool]: 'Think deeply',
  [ToolType.CreateProcedureRunPlan]: 'Create run plan',
  [ToolType.ValidateProcedureRunPlan]: 'Check run plan for errors',
  [ToolType.PromoteProcedureRunPlan]: 'Create run',
  [ToolType.UpdateProcedureRunPlanConditions]: 'Update run plan conditions',
  [ToolType.AddProcedureRunPlanConditions]: 'Add run plan conditions',
  [ToolType.DeleteProcedureRunPlanConditions]: 'Delete run plan conditions',
  [ToolType.UpdateProcedureRunPlanConditionReplicates]: 'Update run plan condition replicates',
  [ToolType.CreateProcedureRunParameterValue]: 'Record parameter value',
  [ToolType.CreateLabAutomationRun]: 'Create lab automation run',
  [ToolType.ExecuteLabAutomationRun]: 'Execute lab automation run',
  [ToolType.GenerateLegacyLabAutomationInput]: 'Generate automation input',
  [ToolType.ProcessLegacyLabAutomationOutput]: 'Process automation output',
  [ToolType.GetLegacyLabAutomationOutputProcessingStatus]: 'Check automation output status',

  // Structure prediction tools
  [ToolType.ListStructurePredictionModels]: 'List structure prediction models',
  [ToolType.StartStructurePredictionBatch]: 'Start structure prediction',
  [ToolType.StartProteinRegistration]: 'Start protein registration',
  [ToolType.GetStructurePredictionBatchResults]: 'Check structure predictions',
  [ToolType.ListSnowflakeTables]: 'List Snowflake tables',
  [ToolType.ExportSnowflakeTableToDataset]: 'Start Snowflake export',
  [ToolType.GetSnowflakeExportStatus]: 'Check Snowflake export',

  // Canvas tools
  [ToolType.CreateCanvasFromHtml]: 'Create canvas',
  [ToolType.UpdateCanvasHtml]: 'Update canvas',

  // Opentrons lab automation tools
  [ToolType.UploadOpentronsProtocol]: 'Upload Opentrons protocol',
  [ToolType.CreateOpentronsRun]: 'Create Opentrons run',
  [ToolType.StartOpentronsRun]: 'Start Opentrons run',
  // Tecan lab automation tools
  [ToolType.RunTecanMethod]: 'Start Tecan method',
  [ToolType.GetTecanOutputFiles]: 'Retrieve Tecan output files',
};

/**
 * Human-readable display names shown after a tool has finished (past tense).
 * Tools that are not displayed in the progress UI have null values.
 */
export const TOOL_DISPLAY_NAMES_COMPLETED: Record<ToolType, string | null> = {
  // Shared
  [ToolType.ToolSearch]: 'Loaded tools',
  [ToolType.Think]: 'Thought',
  [ToolType.IdsToModelsTool]: 'Looked things up',
  [ToolType.SummarizeDocument]: 'Read notebook',
  [ToolType.ReadBenchlingDocumentLegacy]: 'Read notebook',
  [ToolType.SaveArtifact]: 'Saved artifact',
  [ToolType.ListArtifacts]: 'Listed artifacts',
  [ToolType.ViewArtifact]: 'Viewed artifact',
  [ToolType.LoadFileAsArtifact]: 'Loaded file as artifact',
  [ToolType.LoadDocumentAttachmentAsArtifact]: 'Loaded attachment as artifact',
  [ToolType.SubstituteInArtifact]: 'Edited artifact',
  [ToolType.UploadFileToSandbox]: 'Uploaded file to sandbox',
  [ToolType.LoadDropdownOptions]: 'Loaded dropdown options',
  [ToolType.HelpDocs]: 'Searched help docs',
  [ToolType.LoadPackageDocs]: 'Loaded package docs',
  [ToolType.AskWarehouse]: 'Asked warehouse',
  [ToolType.WebSearch]: 'Searched the web',
  [ToolType.SleepTool]: 'Waited',

  // Deep Research
  [ToolType.ResearchSubAgent]: 'Research subagent finished',
  [ToolType.CompleteAnswer]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.CompleteAnswerTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.DeepResearchTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.PublicDataSubAgent]: 'Searched public data',
  [ToolType.SearchTool]: 'Searched Benchling',
  [ToolType.GetWarehouseOverviewTool]: 'Got warehouse overview',
  [ToolType.SearchNotebookEntries]: 'Searched notebook entries',
  [ToolType.SearchSequencesTool]: 'Searched sequences',
  [ToolType.SearchChemicalsTool]: 'Searched chemicals',
  [ToolType.QueryWarehouseTool]: 'Queried warehouse',
  [ToolType.AttachmentTool]: 'Read attachment',
  [ToolType.ReadBlob]: 'Read blob',
  [ToolType.PlottingTool]: 'Created chart',
  [ToolType.GenerateImageTool]: 'Generated image',
  [ToolType.CustomCodeTool]: 'Ran code',
  [ToolType.ExecuteCustomCodePipelineStep]: 'Executed Benchling Custom Code',
  [ToolType.RunCodeTool]: 'Ran code',

  // Skills
  [ToolType.LoadSkill]: 'Used Skill',
  [ToolType.ManageSkill]: 'Managed skill',

  // Analysis & automation template tools
  [ToolType.CreateAnalysisPlan]: 'Created analysis plan',
  [ToolType.CreateAnalysis]: 'Created analysis',
  [ToolType.ApplyTemplateToAnalysis]: 'Applied template to analysis',
  [ToolType.CreateAutomationTemplate]: 'Created automation template',
  [ToolType.EditAutomationTemplate]: 'Edited automation template',
  [ToolType.EditAnalysis]: 'Edited analysis',

  // Data Capture
  [ToolType.Respond]: null, // not in TOOLS_TO_DISPLAY
  // Legacy wire name; see the ToolType enum comment.
  [ToolType.ReadTargetDocument]: 'Read document',
  [ToolType.ReadDocument]: 'Read document',
  [ToolType.ReadDraftDocument]: 'Read document',
  [ToolType.AskQuestion]: 'Asked question',
  [ToolType.InstantiateDocument]: 'Instantiated document',
  [ToolType.SearchEntriesAndTemplates]: 'Searched entries and templates',
  [ToolType.SearchBenchlingObjects]: 'Searched Benchling objects',
  [ToolType.TranscribeFile]: 'Transcribed file',
  [ToolType.ReviewTool]: 'Reviewed',
  [ToolType.EditTableCells]: 'Edited table cells',
  [ToolType.UpdateDocumentContent]: 'Updated document',
  [ToolType.UpdateDocumentLegacy]: 'Updated document',
  [ToolType.InsertDocumentContent]: 'Inserted document content',
  [ToolType.SearchFiles]: 'Searched file content',
  [ToolType.SearchSchemas]: 'Searched schemas',
  [ToolType.FillTables]: 'Filled tables',
  [ToolType.ReadFiles]: 'Read files',
  [ToolType.CreateLookupConfig]: 'Created lookup configuration',

  // Schema administration tools
  [ToolType.CreateDropdownConfig]: 'Created dropdown configuration',
  [ToolType.CreateCustomEntitySchema]: 'Created custom entity schema',
  [ToolType.CreateRunSchema]: 'Created run schema',

  // Agent tools
  [ToolType.ManageRoutine]: 'Managed agent',
  [ToolType.ManageAgent]: 'Managed agent',
  [ToolType.RunAgentDirectly]: 'Started agent run',

  // Co-scientist tools
  [ToolType.ListFolderContents]: 'Listed folder contents',
  [ToolType.CreateFolder]: 'Created folder',
  [ToolType.CreateEntry]: 'Created notebook entry',
  [ToolType.CreateFile]: 'Created file',
  [ToolType.UpdateFile]: 'Updated file',
  [ToolType.ReadFile]: 'Read file',
  [ToolType.CreateAASequences]: 'Created AA sequences',
  [ToolType.CreateDNASequences]: 'Created DNA sequences',
  [ToolType.CreateMolecule]: 'Created molecule',
  [ToolType.CreateCustomEntities]: 'Created custom entities',
  [ToolType.UpdateCustomEntities]: 'Updated custom entities',
  [ToolType.CreateWorkflowTaskGroups]: 'Created workflow task groups',
  [ToolType.UpdateWorkflowTaskGroups]: 'Updated workflow task groups',
  [ToolType.CreateWorkflowTasks]: 'Created workflow tasks',
  [ToolType.UpdateWorkflowTasks]: 'Updated workflow tasks',
  [ToolType.ExecuteWorkflowTasksIntoEntry]: 'Executed workflow tasks',
  [ToolType.ArchiveItem]: 'Archived item',
  [ToolType.RenameItem]: 'Renamed item',
  [ToolType.MoveItemToFolder]: 'Moved item',
  [ToolType.SetTargetEntry]: 'Set target entry',
  [ToolType.AddExistingEntitiesToTable]: 'Added entities to table',
  [ToolType.AddExistingResultsToTable]: 'Added results to table',
  // Legacy wire name; see the ToolType enum comment.
  [ToolType.CreateDocumentTool]: 'Created document',
  [ToolType.CreateDraftDocument]: 'Created document',
  [ToolType.InvokeToolsFromFile]: 'Invoked tools from file',
  [ToolType.RegisterEntities]: 'Registered entities',
  [ToolType.TransferIntoContainers]: 'Transferred into containers',
  [ToolType.CreateContainers]: 'Created containers',
  [ToolType.CreateBoxes]: 'Created boxes',
  [ToolType.UpdateContainerFields]: 'Updated container fields',
  [ToolType.UpdateContainerLocations]: 'Moved containers',
  [ToolType.CreatePlates]: 'Created plates',
  [ToolType.CreateResults]: 'Created results',
  [ToolType.StartChatSession]: 'Started sub-agent session',
  [ToolType.GetChatSession]: 'Checked sub-agent session',
  [ToolType.ListChatSessions]: 'Listed chat sessions',
  [ToolType.ScheduleForFuture]: 'Scheduled for later',
  [ToolType.AskUser]: 'Asked you questions',
  [ToolType.CouncilTool]: 'Thought deeply',
  [ToolType.CreateProcedureRunPlan]: 'Created run plan',
  [ToolType.ValidateProcedureRunPlan]: 'Checked run plan for errors',
  [ToolType.PromoteProcedureRunPlan]: 'Created run',
  [ToolType.UpdateProcedureRunPlanConditions]: 'Updated run plan conditions',
  [ToolType.AddProcedureRunPlanConditions]: 'Added run plan conditions',
  [ToolType.DeleteProcedureRunPlanConditions]: 'Deleted run plan conditions',
  [ToolType.UpdateProcedureRunPlanConditionReplicates]: 'Updated run plan condition replicates',
  [ToolType.CreateProcedureRunParameterValue]: 'Recorded parameter value',
  [ToolType.CreateLabAutomationRun]: 'Created lab automation run',
  [ToolType.ExecuteLabAutomationRun]: 'Executed lab automation run',
  [ToolType.GenerateLegacyLabAutomationInput]: 'Generated automation input',
  [ToolType.ProcessLegacyLabAutomationOutput]: 'Processed automation output',
  [ToolType.GetLegacyLabAutomationOutputProcessingStatus]: 'Checked automation output status',

  // Structure prediction tools
  [ToolType.ListStructurePredictionModels]: 'Listed structure prediction models',
  [ToolType.StartStructurePredictionBatch]: 'Started structure prediction',
  [ToolType.StartProteinRegistration]: 'Started protein registration',
  [ToolType.GetStructurePredictionBatchResults]: 'Checked structure predictions',
  [ToolType.ListSnowflakeTables]: 'Listed Snowflake tables',
  [ToolType.ExportSnowflakeTableToDataset]: 'Started Snowflake export',
  [ToolType.GetSnowflakeExportStatus]: 'Checked Snowflake export',

  // Canvas tools
  [ToolType.CreateCanvasFromHtml]: 'Created canvas',
  [ToolType.UpdateCanvasHtml]: 'Updated canvas',

  // Opentrons lab automation tools
  [ToolType.UploadOpentronsProtocol]: 'Uploaded Opentrons protocol',
  [ToolType.CreateOpentronsRun]: 'Created Opentrons run',
  [ToolType.StartOpentronsRun]: 'Started Opentrons run',
  // Tecan lab automation tools
  [ToolType.RunTecanMethod]: 'Started Tecan method',
  [ToolType.GetTecanOutputFiles]: 'Retrieved Tecan output files',
};

// ============================================================================
// Icons
// ============================================================================

/**
 * Icons for each tool type. Null for tools that are not displayed in the progress UI.
 */
export const TOOL_ICONS: Record<ToolType, BenchlingIconName | null> = {
  // Shared
  [ToolType.ToolSearch]: 'magnifying-glass',
  [ToolType.Think]: 'lightbulb',
  [ToolType.IdsToModelsTool]: 'magnifying-glass',
  [ToolType.SummarizeDocument]: 'file',
  [ToolType.ReadBenchlingDocumentLegacy]: 'file',
  [ToolType.SaveArtifact]: 'file',
  [ToolType.ListArtifacts]: 'file',
  [ToolType.ViewArtifact]: 'file',
  [ToolType.LoadFileAsArtifact]: 'file',
  [ToolType.LoadDocumentAttachmentAsArtifact]: 'paperclip',
  [ToolType.SubstituteInArtifact]: 'edit',
  [ToolType.UploadFileToSandbox]: 'file',
  [ToolType.LoadDropdownOptions]: 'list-check',
  [ToolType.HelpDocs]: 'book',
  [ToolType.LoadPackageDocs]: 'book',
  [ToolType.AskWarehouse]: 'warehouse-full',
  [ToolType.WebSearch]: 'link',
  [ToolType.SleepTool]: 'clock',

  // Deep Research
  [ToolType.ResearchSubAgent]: 'magnifying-glass',
  [ToolType.CompleteAnswer]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.CompleteAnswerTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.DeepResearchTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.PublicDataSubAgent]: 'link',
  [ToolType.SearchTool]: 'magnifying-glass',
  [ToolType.GetWarehouseOverviewTool]: 'warehouse-full',
  [ToolType.SearchNotebookEntries]: 'magnifying-glass',
  [ToolType.SearchSequencesTool]: 'magnifying-glass',
  [ToolType.SearchChemicalsTool]: 'magnifying-glass',
  [ToolType.QueryWarehouseTool]: 'warehouse-full',
  [ToolType.AttachmentTool]: 'file',
  [ToolType.ReadBlob]: 'file',
  [ToolType.PlottingTool]: 'chart-bar',
  [ToolType.GenerateImageTool]: 'image',
  [ToolType.CustomCodeTool]: 'code',
  [ToolType.ExecuteCustomCodePipelineStep]: 'code',
  [ToolType.RunCodeTool]: 'code',

  // Skills
  [ToolType.LoadSkill]: 'file',
  [ToolType.ManageSkill]: 'edit',

  // Analysis & automation template tools
  [ToolType.CreateAnalysisPlan]: 'list-check',
  [ToolType.CreateAnalysis]: 'chart-bar',
  [ToolType.ApplyTemplateToAnalysis]: 'file-import',
  [ToolType.CreateAutomationTemplate]: 'template',
  [ToolType.EditAutomationTemplate]: 'edit',
  [ToolType.EditAnalysis]: 'edit',

  // Data Capture
  [ToolType.Respond]: null, // not in TOOLS_TO_DISPLAY
  // Legacy wire name; see the ToolType enum comment.
  [ToolType.ReadTargetDocument]: 'file',
  [ToolType.ReadDocument]: 'file',
  [ToolType.ReadDraftDocument]: 'file',
  [ToolType.AskQuestion]: 'magnifying-glass',
  [ToolType.InstantiateDocument]: 'file',
  [ToolType.SearchEntriesAndTemplates]: 'magnifying-glass',
  [ToolType.SearchBenchlingObjects]: 'magnifying-glass',
  [ToolType.TranscribeFile]: 'file',
  [ToolType.ReviewTool]: 'check',
  [ToolType.EditTableCells]: 'edit',
  [ToolType.UpdateDocumentContent]: 'edit',
  [ToolType.UpdateDocumentLegacy]: 'edit',
  [ToolType.InsertDocumentContent]: 'plus',
  [ToolType.SearchFiles]: 'magnifying-glass',
  [ToolType.SearchSchemas]: 'magnifying-glass',
  [ToolType.FillTables]: 'edit',
  [ToolType.ReadFiles]: 'file',
  [ToolType.CreateLookupConfig]: 'magnifying-glass',

  // Schema administration tools
  [ToolType.CreateDropdownConfig]: 'list-check',
  [ToolType.CreateCustomEntitySchema]: 'flask',
  [ToolType.CreateRunSchema]: 'process',

  // Agent tools
  [ToolType.ManageRoutine]: 'robot',
  [ToolType.ManageAgent]: 'robot',
  [ToolType.RunAgentDirectly]: 'play',

  // Co-scientist tools
  [ToolType.ListFolderContents]: 'folder-open',
  [ToolType.CreateFolder]: 'folder',
  [ToolType.CreateEntry]: 'file',
  [ToolType.CreateFile]: 'file',
  [ToolType.UpdateFile]: 'edit',
  [ToolType.ReadFile]: 'file',
  [ToolType.CreateAASequences]: 'dna',
  [ToolType.CreateDNASequences]: 'dna',
  [ToolType.CreateMolecule]: 'atom-simple',
  [ToolType.CreateCustomEntities]: 'flask',
  [ToolType.UpdateCustomEntities]: 'edit',
  [ToolType.CreateWorkflowTaskGroups]: 'process',
  [ToolType.UpdateWorkflowTaskGroups]: 'edit',
  [ToolType.CreateWorkflowTasks]: 'process-task',
  [ToolType.UpdateWorkflowTasks]: 'edit',
  [ToolType.ExecuteWorkflowTasksIntoEntry]: 'process-task',
  [ToolType.ArchiveItem]: 'box-archive',
  [ToolType.RenameItem]: 'edit',
  [ToolType.MoveItemToFolder]: 'folder-open',
  [ToolType.SetTargetEntry]: 'file',
  [ToolType.AddExistingEntitiesToTable]: 'table',
  [ToolType.AddExistingResultsToTable]: 'table',
  // Legacy wire name; see the ToolType enum comment.
  [ToolType.CreateDocumentTool]: 'file',
  [ToolType.CreateDraftDocument]: 'file',
  [ToolType.InvokeToolsFromFile]: 'file',
  [ToolType.RegisterEntities]: 'flask',
  [ToolType.TransferIntoContainers]: 'tube',
  [ToolType.CreateContainers]: 'tube',
  [ToolType.CreateBoxes]: 'box',
  [ToolType.UpdateContainerFields]: 'edit',
  [ToolType.UpdateContainerLocations]: 'tube',
  [ToolType.CreatePlates]: 'well-plate',
  [ToolType.CreateResults]: 'assay_result',
  [ToolType.StartChatSession]: 'message-bot',
  [ToolType.GetChatSession]: 'message-bot',
  [ToolType.ListChatSessions]: 'message-bot',
  [ToolType.ScheduleForFuture]: 'clock',
  [ToolType.AskUser]: 'message-bot',
  [ToolType.CouncilTool]: 'brain',
  // null intentional: these tools use the UNKNOWN_TOOL_ICON (wrench) fallback.
  [ToolType.CreateProcedureRunPlan]: null,
  [ToolType.ValidateProcedureRunPlan]: null,
  [ToolType.PromoteProcedureRunPlan]: null,
  [ToolType.UpdateProcedureRunPlanConditions]: null,
  [ToolType.AddProcedureRunPlanConditions]: null,
  [ToolType.DeleteProcedureRunPlanConditions]: null,
  [ToolType.UpdateProcedureRunPlanConditionReplicates]: null,
  [ToolType.CreateProcedureRunParameterValue]: null,
  [ToolType.CreateLabAutomationRun]: 'instrument',
  [ToolType.ExecuteLabAutomationRun]: 'instrument',
  [ToolType.GenerateLegacyLabAutomationInput]: 'instrument',
  [ToolType.ProcessLegacyLabAutomationOutput]: 'instrument',
  [ToolType.GetLegacyLabAutomationOutputProcessingStatus]: 'instrument',

  // Structure prediction tools
  [ToolType.ListStructurePredictionModels]: 'list-check',
  [ToolType.StartStructurePredictionBatch]: 'dna',
  [ToolType.StartProteinRegistration]: 'dna',
  [ToolType.GetStructurePredictionBatchResults]: 'dna',
  [ToolType.ListSnowflakeTables]: 'warehouse-table',
  [ToolType.ExportSnowflakeTableToDataset]: 'export',
  [ToolType.GetSnowflakeExportStatus]: 'dataset',

  // Canvas tools
  [ToolType.CreateCanvasFromHtml]: 'canvas_definition',
  [ToolType.UpdateCanvasHtml]: 'canvas_definition',

  // Opentrons lab automation tools
  [ToolType.UploadOpentronsProtocol]: 'instrument',
  [ToolType.CreateOpentronsRun]: 'instrument',
  [ToolType.StartOpentronsRun]: 'instrument',
  // Tecan lab automation tools
  [ToolType.RunTecanMethod]: 'instrument',
  [ToolType.GetTecanOutputFiles]: 'instrument',
};

/** Icon displayed for unknown/unsupported tool types */
export const UNKNOWN_TOOL_ICON: BenchlingIconName = 'wrench';

// ============================================================================
// MCP (Model Context Protocol) Tools
// ============================================================================

/**
 * MCP tools follow the naming convention: "server_name__tool_name"
 *
 * IMPORTANT: Keep MCP_TOOL_NAME_SEPARATOR in sync with the Python constant:
 * benchling/llm/mcp_client_service/constants.py::SERVER_NAME_SEPARATOR
 */
export const MCP_TOOL_NAME_SEPARATOR = '__';

/** Default icon displayed for all MCP tools */
export const MCP_TOOL_DEFAULT_ICON: BenchlingIconName = 'plug';

/** Default description shown when an MCP tool is executing */
export const MCP_TOOL_DEFAULT_DESCRIPTION = 'Using external tool';

export function isToolType(toolName: string): toolName is ToolType {
  return Object.values(ToolType).includes(toolName as ToolType);
}

export function isMCPTool(toolName: string): boolean {
  return toolName.includes(MCP_TOOL_NAME_SEPARATOR);
}

export function parseMCPToolName(toolName: string): {serverName: string; originalToolName: string} {
  const separatorIndex = toolName.indexOf(MCP_TOOL_NAME_SEPARATOR);
  if (separatorIndex === -1) {
    return {serverName: '', originalToolName: toolName};
  }
  return {
    serverName: toolName.substring(0, separatorIndex),
    originalToolName: toolName.substring(separatorIndex + MCP_TOOL_NAME_SEPARATOR.length),
  };
}

// ============================================================================
// Display Filtering
// ============================================================================

/**
 * Tools that should be displayed in the progress UI.
 * Tools NOT in this list (e.g. Respond, ResearchSubAgent, CompleteAnswer) are
 * either handled specially or are internal implementation details.
 */
const TOOLS_TO_DISPLAY: Array<ToolType> = [
  // Shared
  ToolType.ToolSearch,
  ToolType.Think,
  ToolType.IdsToModelsTool,
  ToolType.SummarizeDocument,
  ToolType.ReadBenchlingDocumentLegacy,
  ToolType.SaveArtifact,
  ToolType.ListArtifacts,
  ToolType.ViewArtifact,
  ToolType.LoadFileAsArtifact,
  ToolType.LoadDocumentAttachmentAsArtifact,
  ToolType.SubstituteInArtifact,
  ToolType.UploadFileToSandbox,
  ToolType.LoadDropdownOptions,
  ToolType.HelpDocs,
  // LoadPackageDocs is intentionally omitted from TOOLS_TO_DISPLAY: it is a
  // citation source the agent calls transparently to load package usage docs
  // into context, not a user-facing progress step. It is still surfaced via
  // citations (`getAllToolCalls`), so the display name and icon above are
  // kept for that path.
  ToolType.AskWarehouse,
  ToolType.WebSearch,
  ToolType.SleepTool,

  // Deep Research
  ToolType.ResearchSubAgent,
  ToolType.SearchTool,
  ToolType.SearchNotebookEntries,
  ToolType.SearchSequencesTool,
  ToolType.SearchChemicalsTool,
  ToolType.GetWarehouseOverviewTool,
  ToolType.QueryWarehouseTool,
  ToolType.AttachmentTool,
  ToolType.ReadBlob,
  ToolType.PublicDataSubAgent,
  ToolType.PlottingTool,
  ToolType.GenerateImageTool,
  ToolType.CustomCodeTool,
  ToolType.ExecuteCustomCodePipelineStep,
  ToolType.RunCodeTool,

  // Skills
  ToolType.LoadSkill,
  ToolType.ManageSkill,

  // Analysis & automation template tools
  ToolType.CreateAnalysisPlan,
  ToolType.CreateAnalysis,
  ToolType.ApplyTemplateToAnalysis,
  ToolType.CreateAutomationTemplate,
  ToolType.EditAutomationTemplate,
  ToolType.EditAnalysis,

  // Data Capture
  // Legacy wire name; see the ToolType enum comment.
  ToolType.ReadTargetDocument,
  ToolType.ReadDocument,
  ToolType.ReadDraftDocument,
  ToolType.AskQuestion,
  ToolType.InstantiateDocument,
  ToolType.SearchEntriesAndTemplates,
  ToolType.SearchBenchlingObjects,
  ToolType.TranscribeFile,
  ToolType.ReviewTool,
  ToolType.EditTableCells,
  ToolType.UpdateDocumentContent,
  ToolType.UpdateDocumentLegacy,
  ToolType.InsertDocumentContent,
  ToolType.SearchFiles,
  ToolType.SearchSchemas,
  ToolType.FillTables,
  ToolType.ReadFiles,
  ToolType.CreateLookupConfig,

  // Schema administration tools
  ToolType.CreateDropdownConfig,
  ToolType.CreateCustomEntitySchema,
  ToolType.CreateRunSchema,

  // Agent tools
  ToolType.ManageRoutine,
  ToolType.ManageAgent,
  ToolType.RunAgentDirectly,

  // Co-scientist tools
  ToolType.ListFolderContents,
  ToolType.CreateFolder,
  ToolType.CreateEntry,
  ToolType.CreateFile,
  ToolType.UpdateFile,
  ToolType.ReadFile,
  ToolType.CreateAASequences,
  ToolType.CreateDNASequences,
  ToolType.CreateMolecule,
  ToolType.CreateCustomEntities,
  ToolType.UpdateCustomEntities,
  ToolType.CreateWorkflowTaskGroups,
  ToolType.UpdateWorkflowTaskGroups,
  ToolType.CreateWorkflowTasks,
  ToolType.UpdateWorkflowTasks,
  ToolType.ExecuteWorkflowTasksIntoEntry,
  ToolType.ArchiveItem,
  ToolType.RenameItem,
  ToolType.MoveItemToFolder,
  ToolType.SetTargetEntry,
  ToolType.AddExistingEntitiesToTable,
  ToolType.AddExistingResultsToTable,
  // Legacy wire name; see the ToolType enum comment.
  ToolType.CreateDocumentTool,
  ToolType.CreateDraftDocument,
  ToolType.InvokeToolsFromFile,
  ToolType.RegisterEntities,
  ToolType.TransferIntoContainers,
  ToolType.CreateContainers,
  ToolType.CreateBoxes,
  ToolType.UpdateContainerFields,
  ToolType.UpdateContainerLocations,
  ToolType.CreatePlates,
  ToolType.CreateResults,
  ToolType.StartChatSession,
  ToolType.GetChatSession,
  ToolType.ListChatSessions,
  ToolType.ScheduleForFuture,
  ToolType.AskUser,
  ToolType.CouncilTool,
  ToolType.CreateProcedureRunPlan,
  ToolType.ValidateProcedureRunPlan,
  ToolType.PromoteProcedureRunPlan,
  ToolType.UpdateProcedureRunPlanConditions,
  ToolType.AddProcedureRunPlanConditions,
  ToolType.DeleteProcedureRunPlanConditions,
  ToolType.UpdateProcedureRunPlanConditionReplicates,
  ToolType.CreateProcedureRunParameterValue,
  ToolType.CreateLabAutomationRun,
  ToolType.ExecuteLabAutomationRun,
  ToolType.GenerateLegacyLabAutomationInput,
  ToolType.ProcessLegacyLabAutomationOutput,
  ToolType.GetLegacyLabAutomationOutputProcessingStatus,

  // Structure prediction tools
  ToolType.ListStructurePredictionModels,
  ToolType.StartStructurePredictionBatch,
  ToolType.StartProteinRegistration,
  ToolType.GetStructurePredictionBatchResults,

  // Snowflake gateway tools
  ToolType.ListSnowflakeTables,
  ToolType.ExportSnowflakeTableToDataset,
  ToolType.GetSnowflakeExportStatus,

  // Canvas tools
  ToolType.CreateCanvasFromHtml,
  ToolType.UpdateCanvasHtml,

  // Opentrons lab automation tools
  ToolType.UploadOpentronsProtocol,
  ToolType.CreateOpentronsRun,
  ToolType.StartOpentronsRun,
  // Tecan lab automation tools
  ToolType.RunTecanMethod,
  ToolType.GetTecanOutputFiles,
];

export type ToolCallDescriptor = Pick<LLMToolCallBlock['value'], 'name' | 'args'>;

export function isCitationGenerationToolCall(
  toolCall: ToolCallDescriptor,
  userMessage: DisplayMessageChatEventNode | null
): boolean {
  return (
    toolCall.name === ToolType.CompleteAnswer &&
    toolCall.args.display_type === 'report' &&
    userMessage != null &&
    userMessage.citationGenerationMode !== CitationGenerationMode.DISABLED
  );
}

/**
 * Whether a tool should be displayed in the progress UI.
 * Returns true for tools in TOOLS_TO_DISPLAY and MCP tools.
 */
export function shouldDisplayTool(toolName: string): boolean {
  return isMCPTool(toolName) || (isToolType(toolName) && TOOLS_TO_DISPLAY.includes(toolName));
}

// ============================================================================
// Display Name Helpers
// ============================================================================

function formatExternalToolName(toolName: string): string {
  return toolName.replace(/_/g, ' ');
}

function getCancelledToolDisplayName(toolName: string, args?: Record<string, unknown>): string {
  if (isMCPTool(toolName)) {
    return `Using AI Connector ${SKIPPED_TOOL_DISPLAY_LABEL}`;
  }

  const inProgressLabel = TOOL_DISPLAY_NAMES_IN_PROGRESS[toolName as ToolType];
  if (inProgressLabel) {
    return enrichDisplayName(toolName, `${inProgressLabel} ${SKIPPED_TOOL_DISPLAY_LABEL}`, args);
  }

  const formattedName = formatExternalToolName(toolName);
  return `Calling ${formattedName} ${SKIPPED_TOOL_DISPLAY_LABEL}`;
}

/**
 * Get the human-readable display name for a tool based on its status.
 * Optionally pass tool call args to enrich display for tools like LoadSkill.
 */
export function getToolDisplayName(
  toolName: string,
  status: ToolCallStatus,
  args?: Record<string, unknown>
): string {
  if (status === ToolCallStatus.CANCELLED) {
    return getCancelledToolDisplayName(toolName, args);
  }

  if (isMCPTool(toolName)) {
    return status === ToolCallStatus.COMPLETED ? 'Used AI Connector' : 'Using AI Connector';
  }

  const descriptions =
    status === ToolCallStatus.COMPLETED ? TOOL_DISPLAY_NAMES_COMPLETED : TOOL_DISPLAY_NAMES_IN_PROGRESS;
  const description = descriptions[toolName as ToolType];
  if (description) {
    return enrichDisplayName(toolName, description, args);
  }

  // For external tools not in the known list, show "Calling/Called {formatted name}"
  const formattedName = formatExternalToolName(toolName);
  return status === ToolCallStatus.COMPLETED ? `Called ${formattedName}` : `Calling ${formattedName}`;
}

/**
 * Extract the skill name from LoadSkill tool call arguments.
 * Returns null if not available.
 */
export function getSkillNameFromArgs(args?: Record<string, unknown>): string | null {
  if (!args) {
    return null;
  }
  const skillName = args.skill_name;
  if (typeof skillName === 'string' && skillName.length > 0) {
    return skillName;
  }
  return null;
}

export function getSkillIdFromArgs(args?: Record<string, unknown>): string | null {
  if (!args) {
    return null;
  }
  const skillId = args.skill_id;
  if (typeof skillId === 'string' && skillId.length > 0) {
    return skillId;
  }
  return null;
}

/** ToolSearch "select:" query prefix. Keep in sync with backend tool_search.py (_parse_query). */
const TOOL_SEARCH_SELECT_PREFIX = 'select:';

/**
 * For a ToolSearch "select:ToolA,ToolB" query, return the explicitly selected tool names.
 * Returns [] for keyword searches (queries that don't start with "select:") or missing args.
 */
export function getToolSearchSelectedToolNames(args?: Record<string, unknown>): Array<string> {
  const query = args?.query;
  if (typeof query !== 'string') {
    return [];
  }
  const trimmed = query.trim();
  if (!trimmed.toLowerCase().startsWith(TOOL_SEARCH_SELECT_PREFIX)) {
    return [];
  }
  return trimmed
    .slice(TOOL_SEARCH_SELECT_PREFIX.length)
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
}

export function getSleepDurationLabel(args?: Record<string, unknown>): string | null {
  const rawSeconds = args?.seconds;
  let seconds: number | null = null;
  if (typeof rawSeconds === 'number' && Number.isFinite(rawSeconds)) {
    seconds = rawSeconds;
  } else if (typeof rawSeconds === 'string' && rawSeconds.trim() !== '') {
    const parsedSeconds = Number(rawSeconds);
    if (Number.isFinite(parsedSeconds)) {
      seconds = parsedSeconds;
    }
  }

  if (seconds == null || seconds < 0) {
    return null;
  }

  const formattedSeconds = Number.isInteger(seconds)
    ? `${seconds}`
    : seconds.toLocaleString('en-US', {maximumFractionDigits: 3});
  return `${formattedSeconds} ${seconds === 1 ? 'second' : 'seconds'}`;
}

/**
 * Enrich a base display name with tool-specific context from args.
 * LoadSkill appends ": <skill name>". ManageSkill overrides the verb
 * based on the action (create/edit).
 */
export function enrichDisplayName(
  toolName: string,
  baseName: string,
  args?: Record<string, unknown>
): string {
  if (toolName === ToolType.LoadSkill) {
    const skillId = getSkillIdFromArgs(args);
    if (skillId != null && isNativeLoadSkill(skillId)) {
      const skillName = getSkillNameFromArgs(args);
      const isCompleted = baseName === TOOL_DISPLAY_NAMES_COMPLETED[ToolType.LoadSkill];
      const isImperative = baseName === TOOL_DISPLAY_NAMES_IMPERATIVE[ToolType.LoadSkill];
      const contextBase = isCompleted ? 'Loaded context' : isImperative ? 'Load context' : 'Loading context';
      if (skillName) {
        return `${contextBase}: ${skillName}`;
      }
      return contextBase;
    }
    const skillName = getSkillNameFromArgs(args);
    if (skillName) {
      return `${baseName}: ${skillName}`;
    }
  }
  if (toolName === ToolType.ManageSkill) {
    return getManageSkillDisplayName(baseName, args);
  }
  if (toolName === ToolType.ToolSearch) {
    // For "select:" queries, surface the chosen tool names in the title (e.g. "Loaded tools:
    // SearchSequencesTool") instead of showing the raw query as a description elsewhere.
    const selectedToolNames = getToolSearchSelectedToolNames(args);
    if (selectedToolNames.length > 0) {
      return `${baseName}: ${selectedToolNames.join(', ')}`;
    }
  }
  if (toolName === ToolType.SleepTool) {
    const durationLabel = getSleepDurationLabel(args);
    if (durationLabel) {
      return `${baseName} for ${durationLabel}`;
    }
  }
  return baseName;
}

const MANAGE_SKILL_COMPLETED_BASE = 'Managed skill';
const MANAGE_SKILL_IMPERATIVE_BASE = 'Manage skill';

function getManageSkillDisplayName(baseName: string, args?: Record<string, unknown>): string {
  const action = args?.action;
  const isCompleted = baseName === MANAGE_SKILL_COMPLETED_BASE;
  const isImperative = baseName === MANAGE_SKILL_IMPERATIVE_BASE;
  if (action === 'create') {
    return isCompleted ? 'Created skill' : isImperative ? 'Create skill' : 'Creating skill';
  }
  if (action === 'edit') {
    return isCompleted ? 'Edited skill' : isImperative ? 'Edit skill' : 'Editing skill';
  }
  return baseName;
}

/**
 * Get a status-independent, imperative-mood task label for the simplified progress timeline.
 */
export function getImperativeToolDisplayName(toolName: string, args?: Record<string, unknown>): string {
  if (isMCPTool(toolName)) {
    const {originalToolName} = parseMCPToolName(toolName);
    return `Use AI Connector: ${originalToolName}`;
  }
  // Guard the lookup: an unguarded index into an object literal can resolve to an inherited
  // Object.prototype member (e.g. `constructor`), which is truthy and would flow through as a
  // display name.
  const description = TOOL_DISPLAY_NAMES_IMPERATIVE.hasOwnProperty(toolName)
    ? TOOL_DISPLAY_NAMES_IMPERATIVE[toolName as ToolType]
    : null;
  if (description) {
    return enrichDisplayName(toolName, description, args);
  }
  return `Call ${formatExternalToolName(toolName)}`;
}

/**
 * Get a basic display name for a tool (in-progress tense).
 * For MCP tools, returns "AI Connector: tool_name". For known tools, returns the
 * in-progress description. For unknown tools, returns the raw tool name.
 * Optionally pass tool call args to enrich display for tools like LoadSkill.
 *
 * Prefer useToolDisplayName hook for rendering, which resolves MCP server names.
 */
export function getBasicToolDisplayName(toolName: string, args?: Record<string, unknown>): string {
  if (isMCPTool(toolName)) {
    const {originalToolName} = parseMCPToolName(toolName);
    return `AI Connector: ${originalToolName}`;
  }
  const description = TOOL_DISPLAY_NAMES_IN_PROGRESS[toolName as ToolType] ?? toolName;
  return enrichDisplayName(toolName, description, args);
}

/**
 * Get the icon for a tool (handles both enum and MCP tools).
 */
export function getToolIcon(toolName: string): BenchlingIconName {
  if (isMCPTool(toolName)) {
    return MCP_TOOL_DEFAULT_ICON;
  }
  return TOOL_ICONS[toolName as ToolType] ?? UNKNOWN_TOOL_ICON;
}
